import {
  repairMissingImageAltInSlides,
  type ContentDocument,
  type ContentSlide,
} from '@course-renderer';
import AppConfig from '../AppConfig';
import { downloadPresignedFile } from '../utils/fileDownloadUtils';
import { fetchAccountById, fetchOrganizationSettings } from './accountsApi';
import http from './http';
import { pollMediaGenerationJob } from './mediaApi';
import {
  COURSE_STATUSES,
  TEMPLATE_TYPES,
  type CourseStatus,
  type TemplateType,
} from '../constants/contentAuthoringEnums';
import type {
  AuthoringCourse,
  AuthoringCoursesQueryParams,
  AuthoringCoursesResponse,
} from '../types/AuthoringCourse';
import {
  TRANSLATION_JOB_STATUSES,
  type TranslationJobStatus,
} from '../types/CourseTranslation';
import { filterAuthoringTranslationLanguages } from '../utils/authoringTranslationLanguages';

const MAX_LIMIT = 100;
const COURSE_STATUS_VALUES: string[] = Object.values(COURSE_STATUSES);
const TEMPLATE_TYPE_VALUES: string[] = Object.values(TEMPLATE_TYPES);

interface CourseListItemDto {
  id: string;
  course_name: string;
  template_id: string;
  org_id: string;
  org_name?: string | null;
  status: string;
  version: number;
  is_locked: boolean;
  slide_count: number;
  customization_level: string | null;
  branding_applied: boolean | null;
  created_at: string;
  updated_at: string;
  created_by: string;
  /** Source course BCP-47 code from GET /courses list. */
  language?: string | null;
  /** All language codes when the list endpoint aggregates translations. */
  languages?: string[] | null;
  /**
   * Derived by the API from `deleted_at`, not a status value (CEE-1066). Optional on the
   * wire so a response from an older deployment maps to `false`, not `undefined`.
   */
  archived?: boolean;
}

interface CoursesResponseDto {
  courses: CourseListItemDto[];
  pagination: {
    page: number;
    limit: number;
    total_count: number;
    total_pages: number;
  };
}

interface TemplateSummaryDto {
  title?: string;
  template_type?: string;
}

function toAuthoringApiUrl(path: string): URL {
  const base = AppConfig.complianceAuthoringApiUrl.replace(/\/$/, '');
  const href = `${base}${path.startsWith('/') ? path : `/${path}`}`;
  return href.startsWith('http') ? new URL(href) : new URL(href, window.location.origin);
}

function toKnownValue<T extends string>(value: string, allowed: string[], fallback: T): T {
  return allowed.includes(value) ? (value as T) : fallback;
}

/**
 * Maps API/DB status strings onto the UI enum (case-insensitive).
 *
 * `approved` is a first-class CourseStatus with its own pill (CEE-1055), so it is NOT aliased to
 * `ready` here — collapsing it would hide that pill. Translation rows are a separate vocabulary
 * and still collapse it, which is what `rawStatus` exists to carry — see
 * TRANSLATION_STATUS_TO_GRID.
 *
 * Tolerates a null or absent status even though the DTOs type it as `string` (CEE-740). A row
 * whose status is missing is a row the whole screen fails to load without this — the throw
 * happens in the mapper, so one bad translation takes down `GET /courses/{id}` for every
 * language. `draft` is the fallback the unknown-value branch already chose for anything it did
 * not recognise.
 */
function normalizeCourseStatus(raw: string | null | undefined): CourseStatus {
  const normalized = (raw ?? '').trim().toLowerCase();
  return toKnownValue<CourseStatus>(normalized, COURSE_STATUS_VALUES, COURSE_STATUSES.DRAFT);
}

async function fetchTemplateSummary(templateId: string): Promise<TemplateSummaryDto | null> {
  const url = toAuthoringApiUrl(`/templates/${templateId}`);
  const response = await http.get(url.toString());
  if (!response.ok) {
    return null;
  }
  try {
    return (await response.json()) as TemplateSummaryDto;
  } catch {
    return null;
  }
}
const templateSummaryCache = new Map<string, TemplateSummaryDto | null>();
const orgDisplayNameCache = new Map<string, string | null>();

/** Resets cached org display names (test-only). */
export function clearAuthoringCourseOrgDisplayNameCacheForTests(): void {
  orgDisplayNameCache.clear();
}

function needsClientNameResolution(dto: CourseListItemDto): boolean {
  const resolved = dto.org_name?.trim();
  if (!resolved) {
    return true;
  }
  return resolved === dto.org_id;
}

async function resolveOrgDisplayName(orgId: string): Promise<string | null> {
  if (orgDisplayNameCache.has(orgId)) {
    return orgDisplayNameCache.get(orgId) ?? null;
  }

  const cacheAndReturn = (name: string | null): string | null => {
    if (name) {
      orgDisplayNameCache.set(orgId, name);
    }
    return name;
  };

  try {
    const response = await fetchAccountById(orgId);
    const accountName = response.data?.name?.trim() || null;
    if (accountName && accountName !== orgId) {
      return cacheAndReturn(accountName);
    }
  } catch {
    /* fall through to organization settings */
  }

  try {
    const settings = await fetchOrganizationSettings(orgId);
    const companyName = settings.companyName?.trim() || null;
    if (companyName) {
      return cacheAndReturn(companyName);
    }
  } catch {
    /* no display name available */
  }

  return null;
}

/** Resolves client display names via GET /accounts/{id} when the list API omits org_name. */
async function enrichCoursesWithClientNames(courses: CourseListItemDto[]): Promise<void> {
  const orgIds = [
    ...new Set(courses.filter(needsClientNameResolution).map((course) => course.org_id)),
  ];
  if (orgIds.length === 0) {
    return;
  }

  const entries: Array<readonly [string, string | null]> = [];
  const CONCURRENCY = 10;

  for (let i = 0; i < orgIds.length; i += CONCURRENCY) {
    const batch = orgIds.slice(i, i + CONCURRENCY);
    const batchEntries = await Promise.all(
      batch.map(async (orgId) => [orgId, await resolveOrgDisplayName(orgId)] as const),
    );
    entries.push(...batchEntries);
  }

  const namesByOrgId = new Map(entries);

  for (const course of courses) {
    if (!needsClientNameResolution(course)) {
      continue;
    }
    const name = namesByOrgId.get(course.org_id);
    if (name) {
      course.org_name = name;
    }
  }
}

async function enrichCoursesWithTemplateMeta(
  courses: CourseListItemDto[],
): Promise<Map<string, TemplateSummaryDto>> {
  const uniqueTemplateIds = [...new Set(courses.map((course) => course.template_id))];
  const entries = await Promise.all(
    uniqueTemplateIds.map(async (templateId) => {
      const cached = templateSummaryCache.get(templateId);
      if (cached !== undefined) {
        return [templateId, cached] as const;
      }

      const summary = await fetchTemplateSummary(templateId).catch(() => null);
      templateSummaryCache.set(templateId, summary);
      return [templateId, summary] as const;
    }),
  );

  const map = new Map<string, TemplateSummaryDto>();
  for (const [templateId, summary] of entries) {
    if (summary) {
      map.set(templateId, summary);
    }
  }
  return map;
}

function buildCourseLanguageCodes(
  sourceLanguage: string | null | undefined,
  translationLanguages: readonly (string | null | undefined)[],
): string[] {
  const codes = new Set<string>();
  if (sourceLanguage) {
    codes.add(sourceLanguage);
  }
  for (const language of translationLanguages) {
    if (language) {
      codes.add(language);
    }
  }
  return [...codes].sort((a, b) => a.localeCompare(b));
}

function listItemNeedsLanguageAggregation(course: CourseListItemDto): boolean {
  return course.languages == null;
}

/** Loads source + translation language codes when GET /courses omits `languages`. */
async function fetchCourseLanguageCodes(courseId: string): Promise<string[]> {
  const url = toAuthoringApiUrl(`/courses/${courseId}`);
  const response = await http.get(url.toString());
  if (!response.ok) {
    return [];
  }

  try {
    const body = (await response.json()) as AuthoringCourseDetailDto;
    return buildCourseLanguageCodes(
      body.language,
      (body.translations ?? []).map((translation) => translation.language),
    );
  } catch {
    return [];
  }
}

async function enrichListCoursesWithLanguages(courses: CourseListItemDto[]): Promise<void> {
  const coursesNeedingLanguages = courses.filter(listItemNeedsLanguageAggregation);
  if (coursesNeedingLanguages.length === 0) {
    return;
  }

  const languageEntries: Array<readonly [string, string[]]> = [];
  const CONCURRENCY = 10;

  for (let i = 0; i < coursesNeedingLanguages.length; i += CONCURRENCY) {
    const batch = coursesNeedingLanguages.slice(i, i + CONCURRENCY);
    const entries = await Promise.all(
      batch.map(async (course) => {
        const languages = await fetchCourseLanguageCodes(course.id);
        return [course.id, languages] as const;
      }),
    );
    languageEntries.push(...entries);
  }
  const languagesByCourseId = new Map(languageEntries);

  for (const course of courses) {
    if (!listItemNeedsLanguageAggregation(course)) {
      continue;
    }
    const aggregated = languagesByCourseId.get(course.id);
    course.languages =
      aggregated && aggregated.length > 0
        ? aggregated
        : course.language
          ? [course.language]
          : [];
  }
}

function toAuthoringCourse(
  dto: CourseListItemDto,
  templateMeta: Map<string, TemplateSummaryDto>,
): AuthoringCourse {
  const templateSummary = templateMeta.get(dto.template_id);
  const templateType = templateSummary?.template_type
    ? toKnownValue<TemplateType>(
        templateSummary.template_type,
        TEMPLATE_TYPE_VALUES,
        TEMPLATE_TYPES.STANDALONE,
      )
    : null;

  return {
    id: dto.id,
    name: dto.course_name,
    type: templateType,
    status: normalizeCourseStatus(dto.status),
    client: dto.org_name?.trim() || dto.org_id,
    template: templateSummary?.title ?? dto.template_id,
    languages:
      dto.languages && dto.languages.length > 0
        ? dto.languages
        : dto.language
          ? [dto.language]
          : [],
    version: dto.version,
    updatedAt: dto.updated_at,
    templateId: dto.template_id,
    orgId: dto.org_id,
    // Archiving does not rewrite `status`, so an archived course keeps reporting the
    // draft/ready/submitted it reached and keeps its Ver value (CEE-1066).
    archived: dto.archived === true,
  };
}

async function parseErrorMessage(response: Response, fallback: string): Promise<string> {
  let message = fallback;
  try {
    const body = await response.json();
    if (body?.error?.message) {
      message = body.error.message;
    } else if (body?.message) {
      message = body.message;
    }
  } catch {
    /* keep fallback */
  }
  return message;
}

/**
 * Fetches the paginated list of authoring courses (`GET /courses`).
 */
export async function fetchAuthoringCourses(
  params: AuthoringCoursesQueryParams = {},
): Promise<AuthoringCoursesResponse> {
  const { search, page = 1, limit = 10, status, orgId, archived } = params;
  const requestedLimit = Math.min(limit, MAX_LIMIT);

  const url = toAuthoringApiUrl('/courses');
  url.searchParams.set('page', String(page));
  url.searchParams.set('limit', String(requestedLimit));
  const query = search?.trim();
  if (query) {
    url.searchParams.set('q', query);
  }
  if (status) {
    url.searchParams.set('status', status);
  }
  if (orgId) {
    url.searchParams.set('org_id', orgId);
  }
  // Left off when unset so callers that only want live courses keep the API's `active`
  // default without having to name it.
  if (archived) {
    url.searchParams.set('archived', archived);
  }

  const response = await http.get(url.toString());
  if (!response.ok) {
    throw new Error(await parseErrorMessage(response, `Failed to load courses (${response.status})`));
  }

  const body: CoursesResponseDto = await response.json();
  const courses = body.courses ?? [];
  await enrichListCoursesWithLanguages(courses);
  await enrichCoursesWithClientNames(courses);
  const templateMeta = await enrichCoursesWithTemplateMeta(courses);

  return {
    data: courses.map((course) => toAuthoringCourse(course, templateMeta)),
    pagination: {
      page: body.pagination?.page ?? page,
      limit: body.pagination?.limit ?? requestedLimit,
      total: body.pagination?.total_count ?? 0,
    },
  };
}

/** Soft-deletes a course (`DELETE /courses/{id}`). */
export async function archiveAuthoringCourse(courseId: string): Promise<void> {
  const url = toAuthoringApiUrl(`/courses/${courseId}`);
  const response = await http.delete(url.toString());
  if (!response.ok) {
    throw new Error(await parseErrorMessage(response, `Failed to archive course (${response.status})`));
  }
}

/** Downloads course JSON content (`GET /courses/{id}/content`). */
export async function exportAuthoringCourse(courseId: string, courseName: string): Promise<void> {
  const url = toAuthoringApiUrl(`/courses/${courseId}/content`);
  const response = await http.get(url.toString());
  if (!response.ok) {
    throw new Error(await parseErrorMessage(response, `Failed to export course (${response.status})`));
  }

  const content = await response.json();
  const blob = new Blob([JSON.stringify(content, null, 2)], { type: 'application/json' });
  const objectUrl = URL.createObjectURL(blob);

  const fileNameBase =
    courseName
      .trim()
      .replace(/[^a-z0-9-_]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase() || 'course';

  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = `${fileNameBase}-export.json`;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();

  setTimeout(() => {
    URL.revokeObjectURL(objectUrl);
    anchor.remove();
  }, 100);
}

/** The 202 body of `POST /courses/{id}/submit`. */
export interface AuthoringCourseSubmission {
  courseId: string;
  status: string;
  version: number;
  scormVersion: string;
  /** BCP-47 codes packaged, source language first. */
  languages: string[];
  /** What this submission's Customized Content entry is called. */
  customContentName: string;
  jobId: string;
  pollUrl: string;
  estimatedCompletionSeconds: number;
}

interface SubmitResponseDto {
  course_id: string;
  status: string;
  version: number;
  scorm_version: string;
  languages: string[];
  custom_content_name: string;
  job_id: string;
  poll_url: string;
  estimated_completion_seconds: number;
}

/**
 * Submits a course for review (`POST /courses/{id}/submit`).
 *
 * ASYNC, AND REPEATABLE. The response is a 202: the status flips immediately, and a SCORM
 * package is built in the background under `job_id` (poll it via `GET /jobs/{id}`). The same
 * course is submitted many times with different language sets — every submission is its own
 * package and its own Customized Content entry — so this is a normal action on a course that is
 * already `submitted`, not a one-way door.
 */
export interface SubmitAuthoringCourseRequest {
  languages: string[];
  scormVersion: '1.2' | '2004-3rd' | '2004-4th';
  notes?: string;
}

export async function submitAuthoringCourse(
  courseId: string,
  request: SubmitAuthoringCourseRequest,
): Promise<AuthoringCourseSubmission> {
  const url = toAuthoringApiUrl(`/courses/${courseId}/submit`);
  const response = await http.post(
    url.toString(),
    JSON.stringify({
      languages: request.languages,
      scorm_version: request.scormVersion,
      ...(request.notes ? { notes: request.notes } : {}),
    }),
  );
  if (!response.ok) {
    throw new Error(
      await parseErrorMessage(response, `Failed to submit course for review (${response.status})`),
    );
  }
  const body = (await response.json()) as SubmitResponseDto;
  return {
    courseId: body.course_id,
    status: body.status,
    version: body.version,
    scormVersion: body.scorm_version,
    languages: body.languages ?? [],
    customContentName: body.custom_content_name,
    jobId: body.job_id,
    pollUrl: body.poll_url,
    estimatedCompletionSeconds: body.estimated_completion_seconds,
  };
}

/** Opens the translations workflow for a course. */
export function getAuthoringCourseTranslationsPath(courseId: string): string {
  return `/authoring/courses/${courseId}/translations`;
}

interface TranslationListItemDto {
  id: string;
  course_name: string;
  language: string | null;
  status: string;
  version: number;
  is_locked: boolean;
  slide_count: number;
  source_version: number | null;
  is_stale: boolean;
  /** Derived from `deleted_at` (CEE-1066); optional so an older deployment maps to false. */
  archived?: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  reviewed_by_display_name: string | null;
  translated_at: string | null;
}

interface TranslationJobListItemDto {
  job_id: string;
  language: string;
  course_id: string | null;
  status: string;
  progress_pct: number | null;
  progress_message: string | null;
  error_message: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

interface AuthoringCourseDetailDto {
  id: string;
  course_name: string;
  org_id: string;
  status: string;
  version: number;
  is_locked: boolean;
  language: string | null;
  /** NULL for a source course; the translated-from course id for a translation. */
  parent_course_id?: string | null;
  /** Derived from `deleted_at` (CEE-1066); optional so an older deployment maps to false. */
  archived?: boolean;
  updated_at: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  reviewed_by_display_name: string | null;
  translations: TranslationListItemDto[];
  translation_jobs: TranslationJobListItemDto[];
}

export interface AuthoringCourseTranslation {
  id: string;
  courseName: string;
  language: string;
  /**
   * Widened past `CourseStatus` because a translation's vocabulary is not a source course's: an
   * in-flight or failed language is reported with the job status the grid already renders. See
   * TRANSLATION_STATUS_TO_GRID.
   */
  status: CourseStatus | TranslationJobStatus;
  /**
   * The backend value verbatim, before TRANSLATION_STATUS_TO_GRID collapses it (CEE-740).
   *
   * Carried because that mapping is lossy where it matters: `ready_for_approval` and `approved`
   * both become `ready`, and the grid tells them apart only by `reviewedAt`. The row actions
   * need the distinction directly — Edit is allowed on a language awaiting approval and refused
   * on an approved one, and Retranslate has to send `replace_existing` for an approved language
   * (it archives the approval it invalidates) and must NOT for any other, where a retranslate
   * reuses the row. Inferring that from `reviewedAt` would break the moment a row is approved
   * without one.
   */
  rawStatus: string;
  isLocked: boolean;
  isStale: boolean;
  /**
   * Archived (soft-deleted) — derived from `deleted_at`, orthogonal to `status`/`rawStatus`,
   * which keep the value the language had reached (CEE-1066).
   */
  archived: boolean;
  translatedAt: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  updatedAt: string;
}

export interface AuthoringCourseTranslationJob {
  jobId: string;
  language: string;
  courseId: string | null;
  status: string;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AuthoringCourseDetail {
  id: string;
  courseName: string;
  orgId: string;
  status: CourseStatus;
  version: number;
  isLocked: boolean;
  sourceLanguage: string | null;
  /** NULL for a source course; the English course this translation was produced from. */
  parentCourseId: string | null;
  /**
   * The course itself is archived (CEE-1066). Everything about it is view-only — its own
   * archive is a 409 and its edits are refused at the API — and its languages come back
   * archived with it.
   */
  archived: boolean;
  updatedAt: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  translations: AuthoringCourseTranslation[];
  translationJobs: AuthoringCourseTranslationJob[];
}

export interface AuthoringLanguage {
  code: string;
  name: string;
  nativeName: string | null;
  direction: string;
}

/**
 * The translation vocabulary the API returns (CEE-740), mapped onto the grid's model.
 *
 * A translation's statuses are disjoint from a source course's — `translating -> failed |
 * ready_for_approval -> approved`, never `draft` — so `normalizeCourseStatus` would drop all
 * four through its unknown-value fallback and report every language as Draft.
 *
 * The grid does not have a status per backend value; it renders `ready` plus `reviewedAt` as
 * Ready-for-approval vs Approved, and reuses the job vocabulary for in-flight and failed rows.
 * So this maps onto what the grid already reads, which is why `failed` becomes the job's
 * `failed` — that is the value `canRetranslate` keys on, and Retranslate is the whole action a
 * failed language exists to offer.
 *
 * Deliberately NOT a blanket draft -> ready rewrite: `translating` and `failed` would be swept
 * into it too, showing a language that is still running, or that failed outright, as ready for a
 * reviewer to approve.
 */
const TRANSLATION_STATUS_TO_GRID: Record<string, CourseStatus | TranslationJobStatus> = {
  ready_for_approval: COURSE_STATUSES.READY,
  approved: COURSE_STATUSES.READY,
  failed: TRANSLATION_JOB_STATUSES.FAILED,
  translating: TRANSLATION_JOB_STATUSES.RUNNING,
};

function toAuthoringCourseTranslation(dto: TranslationListItemDto): AuthoringCourseTranslation {
  const raw = dto.status?.trim().toLowerCase() ?? '';
  const status = TRANSLATION_STATUS_TO_GRID[raw] ?? normalizeCourseStatus(dto.status);

  return {
    id: dto.id,
    courseName: dto.course_name,
    language: dto.language ?? '',
    status,
    rawStatus: raw,
    isLocked: dto.is_locked,
    isStale: dto.is_stale,
    // Absent from an older API deployment reads as not-archived, which is what every row
    // was before CEE-1066 taught the list to include them.
    archived: dto.archived === true,
    translatedAt: dto.translated_at,
    reviewedBy: dto.reviewed_by_display_name ?? dto.reviewed_by,
    reviewedAt: dto.reviewed_at,
    updatedAt: dto.updated_at,
  };
}

function toAuthoringCourseTranslationJob(dto: TranslationJobListItemDto): AuthoringCourseTranslationJob {
  return {
    jobId: dto.job_id,
    language: dto.language,
    courseId: dto.course_id,
    status: dto.status,
    errorMessage: dto.error_message,
    createdAt: dto.created_at,
    updatedAt: dto.completed_at ?? dto.started_at ?? dto.created_at,
  };
}

/** Full course metadata with embedded translations (`GET /courses/{id}`). */
export async function fetchAuthoringCourse(courseId: string): Promise<AuthoringCourseDetail> {
  const url = toAuthoringApiUrl(`/courses/${courseId}`);
  // Archived languages stay in the translations grid, labelled Archived and with no actions
  // (CEE-1066) — the archive dialog promises exactly that — so the detail read asks for both
  // sides of the archive line rather than the API's `active` default.
  url.searchParams.set('archived', 'all');
  const response = await http.get(url.toString());
  if (!response.ok) {
    throw new Error(await parseErrorMessage(response, `Failed to load course (${response.status})`));
  }

  const body = (await response.json()) as AuthoringCourseDetailDto;
  return {
    id: body.id,
    courseName: body.course_name,
    orgId: body.org_id,
    status: normalizeCourseStatus(body.status),
    version: body.version,
    isLocked: body.is_locked,
    sourceLanguage: body.language,
    parentCourseId: body.parent_course_id ?? null,
    archived: body.archived === true,
    updatedAt: body.updated_at,
    reviewedBy: body.reviewed_by_display_name ?? body.reviewed_by,
    reviewedAt: body.reviewed_at,
    translations: (body.translations ?? []).map(toAuthoringCourseTranslation),
    translationJobs: (body.translation_jobs ?? []).map(toAuthoringCourseTranslationJob),
  };
}

/** Lists Phase-1 translation target languages (`GET /languages`). */
export async function fetchAuthoringLanguages(): Promise<AuthoringLanguage[]> {
  const url = toAuthoringApiUrl('/languages');
  const response = await http.get(url.toString());
  if (!response.ok) {
    throw new Error(await parseErrorMessage(response, `Failed to load languages (${response.status})`));
  }

  const body = (await response.json()) as {
    languages: Array<{
      code: string;
      name: string;
      native_name: string | null;
      direction: string;
    }>;
  };

  return filterAuthoringTranslationLanguages(
    (body.languages ?? []).map((language) => ({
      code: language.code,
      name: language.name,
      nativeName: language.native_name,
      direction: language.direction,
    })),
  );
}

export interface TranslateAuthoringCourseRequest {
  languageCodes: string[];
  replaceExisting?: boolean;
}

export interface TranslateAuthoringCourseJob {
  jobId: string;
  courseId: string;
  language: string;
  status: string;
  pollUrl: string;
}

export interface TranslateAuthoringCourseResponse {
  courseId: string;
  translations: TranslateAuthoringCourseJob[];
}

/** Enqueues translation jobs per language (`POST /courses/{id}/translate`). */
export async function translateAuthoringCourse(
  courseId: string,
  request: TranslateAuthoringCourseRequest,
): Promise<TranslateAuthoringCourseResponse> {
  const url = toAuthoringApiUrl(`/courses/${courseId}/translate`);
  const response = await http.post(
    url.toString(),
    JSON.stringify({
      language_codes: request.languageCodes,
      replace_existing: request.replaceExisting ?? false,
    }),
  );

  if (response.status !== 202) {
    throw new Error(
      await parseErrorMessage(response, `Failed to start translations (${response.status})`),
    );
  }

  const body = (await response.json()) as {
    course_id: string;
    translations: Array<{
      job_id: string;
      course_id: string;
      language: string;
      status: string;
      poll_url: string;
    }>;
  };

  return {
    courseId: body.course_id,
    translations: (body.translations ?? []).map((job) => ({
      jobId: job.job_id,
      courseId: job.course_id,
      language: job.language,
      status: job.status,
      pollUrl: job.poll_url,
    })),
  };
}

export interface StartAuthoringCourseExportRequest {
  languages: string[];
  scormVersions: Array<'1.2' | '2004-3rd' | '2004-4th'>;
  packaging: 'single' | 'separate';
}

export interface StartAuthoringCourseExportJob {
  scormVersion: string;
  jobId: string;
  status: string;
  pollUrl: string;
}

export interface StartAuthoringCourseExportResponse {
  courseId: string;
  version: number;
  defaultLanguage: string;
  exports: StartAuthoringCourseExportJob[];
}

/** Enqueues SCORM packaging jobs (`POST /courses/{id}/export`). */
export async function startAuthoringCourseExport(
  courseId: string,
  request: StartAuthoringCourseExportRequest,
): Promise<StartAuthoringCourseExportResponse> {
  const url = toAuthoringApiUrl(`/courses/${courseId}/export`);
  const response = await http.post(
    url.toString(),
    JSON.stringify({
      languages: request.languages,
      scorm_versions: request.scormVersions,
      packaging: request.packaging,
    }),
  );

  if (response.status !== 202) {
    throw new Error(
      await parseErrorMessage(response, `Failed to start export (${response.status})`),
    );
  }

  const body = (await response.json()) as {
    course_id: string;
    version: number;
    default_language: string;
    exports: Array<{
      scorm_version: string;
      job_id: string;
      status: string;
      poll_url: string;
    }>;
  };

  return {
    courseId: body.course_id,
    version: body.version,
    defaultLanguage: body.default_language,
    exports: (body.exports ?? []).map((item) => ({
      scormVersion: item.scorm_version,
      jobId: item.job_id,
      status: item.status,
      pollUrl: item.poll_url,
    })),
  };
}

export interface AuthoringCourseExportDownload {
  exportId: string;
  courseId: string;
  downloadUrl: string;
  downloadUrlExpiresAt: string;
  filename: string;
  bytes: number | null;
  scormVersion: string | null;
  courseVersion: number | null;
}

/** Presigns a succeeded SCORM zip (`GET /exports/{id}/download`). The id is the export job id. */
export async function downloadAuthoringCourseExport(
  exportId: string,
): Promise<AuthoringCourseExportDownload> {
  const url = toAuthoringApiUrl(`/exports/${exportId}/download`);
  const response = await http.get(url.toString());
  if (!response.ok) {
    throw new Error(
      await parseErrorMessage(response, `Failed to download export (${response.status})`),
    );
  }

  const body = (await response.json()) as {
    export_id: string;
    course_id: string;
    download_url: string;
    download_url_expires_at: string;
    filename: string;
    bytes: number | null;
    scorm_version: string | null;
    course_version: number | null;
  };

  return {
    exportId: body.export_id,
    courseId: body.course_id,
    downloadUrl: body.download_url,
    downloadUrlExpiresAt: body.download_url_expires_at,
    filename: body.filename,
    bytes: body.bytes,
    scormVersion: body.scorm_version,
    courseVersion: body.course_version,
  };
}

/**
 * Polls each packaging job, then triggers a standard browser download of the zip
 * (Content-Disposition: attachment on the presigned URL).
 */
export async function completeAuthoringCourseExportDownloads(
  jobs: StartAuthoringCourseExportJob[],
  options?: { signal?: AbortSignal },
): Promise<void> {
  for (const job of jobs) {
    if (options?.signal?.aborted) {
      throw new Error('Aborted');
    }

    const polled = await pollMediaGenerationJob(job.jobId, { signal: options?.signal });
    if (options?.signal?.aborted) {
      throw new Error('Aborted');
    }
    if (polled.status !== 'succeeded') {
      throw new Error(polled.error_message || `Export job ${job.jobId} failed`);
    }

    const download = await downloadAuthoringCourseExport(job.jobId);
    downloadPresignedFile(download.downloadUrl, download.filename);
  }
}

/**
 * Approves a translation (`POST /courses/{id}/approve`).
 *
 * NOT `/courses/{id}/ready` — the two endpoints belong to the two different vocabularies
 * (CEE-740). `/ready` is the source author's own statement of completion and 400s outright on a
 * row with a `parent_course_id`; `/approve` is the reviewer's sign-off on one language and is
 * only ever asked of a translation.
 */
export async function approveAuthoringCourseTranslation(courseId: string): Promise<void> {
  const url = toAuthoringApiUrl(`/courses/${courseId}/approve`);
  const response = await http.post(url.toString(), '{}');
  if (!response.ok) {
    throw new Error(
      await parseErrorMessage(response, `Failed to approve translation (${response.status})`),
    );
  }
}

/**
 * Marks a SOURCE course ready (`POST /courses/{id}/ready`).
 *
 * The author's own statement that the English is finished, and the only way a `draft` course
 * reaches `ready` — which `submit` requires (CEE-740). Distinct from
 * `approveAuthoringCourseTranslation` because the backend refuses each endpoint the other's
 * kind of row: `/ready` 400s on anything with a `parent_course_id`, `/approve` 400s on a source
 * course. Posting the source through `/approve` therefore left a draft with no route to
 * `ready`, and so no route to submission at all.
 */
export async function markAuthoringCourseSourceReady(courseId: string): Promise<void> {
  const url = toAuthoringApiUrl(`/courses/${courseId}/ready`);
  const response = await http.post(url.toString(), '{}');
  if (!response.ok) {
    throw new Error(
      await parseErrorMessage(response, `Failed to mark course as ready (${response.status})`),
    );
  }
}

/**
 * Response from GET /courses/{id}/content (CEE-746).
 *
 * Same shape as template content: the S3 document plus version metadata. Left as
 * `ContentDocument` rather than validated here so the shared editor can render it.
 */
export interface CourseBrandingLogoUrlResponse {
  s3_path?: string;
  preview_url: string;
  preview_url_expires_at: string;
}

/** Response from `POST /courses/{id}/sync-logo` (CEE-1267). */
export interface SyncCourseLogoResponse {
  id: string;
  logo_image_key: string;
  /** False when the course already held the org's current logo — a no-op, not a failure. */
  changed: boolean;
  /** True only when the extension moved, which is the case that rewrites content.json. */
  key_changed: boolean;
  content_updated: boolean;
  /**
   * Freshly presigned preview of the synced logo.
   *
   * Load-bearing, not a convenience: the usual path overwrites the bytes under the SAME S3
   * key, so neither the logo query key nor the browser's image cache changes, and without a
   * new signed URL a successful sync repaints the old logo until a hard reload.
   */
  branding_logo?: CourseBrandingLogoUrlResponse;
}

/**
 * Pulls the client's current Settings logo into a course version (`POST /courses/{id}/sync-logo`).
 *
 * A course copies the org logo into its own S3 prefix when it is created, so it keeps showing
 * the logo the client had then — this is the only way it picks up a rebrand. Source courses
 * only; the backend 400s a translation, which renders the source's logo anyway.
 */
export async function syncAuthoringCourseLogo(courseId: string): Promise<SyncCourseLogoResponse> {
  const url = toAuthoringApiUrl(`/courses/${courseId}/sync-logo`);
  const response = await http.post(url.toString(), '{}');
  if (!response.ok) {
    throw new Error(await parseErrorMessage(response, `Failed to sync logo (${response.status})`));
  }
  return response.json();
}

export interface CourseContentResponse {
  course_id: string;
  version: number;
  s3_content_path: string;
  content: ContentDocument;
  /** Presigned inline URL for the course branding logo when one exists (CEE-1142). */
  branding_logo?: CourseBrandingLogoUrlResponse;
}

/**
 * Fetches a course's slide content for the editor.
 *
 * A 404 is a normal state, not a failure: `content_not_found` means generation has not
 * written the document yet. Both 404 cases return null so the editor can show an empty
 * state instead of an error alert — same contract as `fetchTemplateContent`.
 */
export async function fetchCourseContent(
  courseId: string,
): Promise<CourseContentResponse | null> {
  const url = toAuthoringApiUrl(`/courses/${courseId}/content`);
  const response = await http.get(url.toString());

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(await parseErrorMessage(response, `Failed to load course content (${response.status})`));
  }

  const body: CourseContentResponse = await response.json();

  return {
    ...body,
    content: {
      ...body.content,
      slides: Array.isArray(body.content?.slides) ? body.content.slides : [],
    },
  };
}

/** Presigned inline URL for a course branding logo (`GET /courses/{id}/branding-logo-url`). */
export async function fetchCourseBrandingLogoUrl(
  courseId: string,
): Promise<CourseBrandingLogoUrlResponse> {
  const url = toAuthoringApiUrl(`/courses/${courseId}/branding-logo-url`);
  const response = await http.get(url.toString());

  if (!response.ok) {
    throw new Error(
      await parseErrorMessage(response, `Failed to load course branding logo (${response.status})`),
    );
  }

  return (await response.json()) as CourseBrandingLogoUrlResponse;
}

/** Request body for PUT /courses/{id} (CEE-746). */
export interface UpdateAuthoringCourseRequest {
  content: {
    total_slides: number;
    slides: ContentSlide[];
    [key: string]: unknown;
  };
  expected_version?: number;
}

/** Response from PUT /courses/{id} (CEE-746). */
export interface UpdateAuthoringCourseResponse {
  id: string;
  version: number;
  s3_content_path: string;
  created_new_draft: boolean;
  status: 'draft';
}

/**
 * Builds the PUT body for persisting editor changes.
 * Spreads the loaded document root so `schema_version` and other fields round-trip unchanged.
 */
export function buildUpdateCourseRequest(
  loadedContent: ContentDocument,
  slides: ContentSlide[],
  expectedVersion?: number,
): UpdateAuthoringCourseRequest {
  const repairedSlides = repairMissingImageAltInSlides(slides);
  return {
    content: {
      ...loadedContent,
      slides: repairedSlides,
      total_slides: repairedSlides.length,
    },
    ...(expectedVersion !== undefined ? { expected_version: expectedVersion } : {}),
  };
}

/**
 * Updates course content via manual Spark edits (`PUT /courses/{id}`).
 * Unlocked drafts overwrite the current version path; locked courses bump to v{N+1}.
 */
export async function updateAuthoringCourse(
  courseId: string,
  request: UpdateAuthoringCourseRequest,
): Promise<UpdateAuthoringCourseResponse> {
  const url = toAuthoringApiUrl(`/courses/${courseId}`);
  const response = await http.put(url.toString(), JSON.stringify(request));

  if (!response.ok) {
    throw new Error(await parseErrorMessage(response, `Failed to update course (${response.status})`));
  }

  return response.json();
}

export interface CreateAuthoringCourseRequest {
  templateId: string;
  orgId: string;
  courseName: string;
  applyBranding?: boolean;
}

export interface CreateAuthoringCourseResponse {
  courseId: string;
  jobId: string;
  status: string;
  pollUrl: string;
  estimatedCompletionSeconds: number;
}

interface CreateAuthoringCourseResponseDto {
  course_id: string;
  job_id: string;
  status: string;
  poll_url: string;
  estimated_completion_seconds: number;
}

/**
 * Creates a client course from an approved template (`POST /courses`).
 * Returns 202 with an async job envelope; poll via GET /jobs/{id}.
 */
export async function createAuthoringCourse(
  request: CreateAuthoringCourseRequest,
): Promise<CreateAuthoringCourseResponse> {
  const url = toAuthoringApiUrl('/courses');
  const response = await http.post(
    url.toString(),
    JSON.stringify({
      template_id: request.templateId,
      org_id: request.orgId,
      course_name: request.courseName,
      apply_branding: request.applyBranding ?? true,
    }),
  );

  if (response.status !== 202) {
    throw new Error(
      await parseErrorMessage(response, `Failed to create course (${response.status})`),
    );
  }

  const body = (await response.json()) as CreateAuthoringCourseResponseDto;
  return {
    courseId: body.course_id,
    jobId: body.job_id,
    status: body.status,
    pollUrl: body.poll_url,
    estimatedCompletionSeconds: body.estimated_completion_seconds,
  };
}

/** Request body for PATCH /courses/{id}/metadata (CEE-1101). */
export interface RenameCourseRequest {
  course_name: string;
}

/** Response from PATCH /courses/{id}/metadata (CEE-1101). */
export interface RenameCourseResponse {
  id: string;
  course_name: string;
  version: number;
}

/**
 * Renames a source course (CEE-1101).
 * Updates only course_name metadata; does not affect content or version.
 */
export async function renameCourse(
  courseId: string,
  newName: string,
): Promise<{ courseId: string; courseName: string; version: number }> {
  const url = toAuthoringApiUrl(`/courses/${courseId}/metadata`);

  const payload: RenameCourseRequest = {
    course_name: newName.trim(),
  };

  const response = await http.patch(url.toString(), JSON.stringify(payload));

  if (!response.ok) {
    let message = `Failed to rename course (${response.status})`;
    try {
      const body = await response.json();
      if (body?.error?.message) {
        message = body.error.message;
      }
    } catch {
      /* keep the status-based message */
    }
    throw new Error(message);
  }

  const body = (await response.json()) as RenameCourseResponse;
  return {
    courseId: body.id,
    courseName: body.course_name,
    version: body.version,
  };
}

export interface DuplicateAuthoringCourseRequest {
  courseName: string;
  orgId: string;
}

export interface DuplicateAuthoringCourseResponse {
  courseId: string;
  courseName: string;
  orgId: string;
  status: string;
}

/**
 * Duplicates a source course (`POST /courses/{id}/duplicate`).
 * Returns 201 with the new draft course metadata.
 */
export async function duplicateAuthoringCourse(
  courseId: string,
  request: DuplicateAuthoringCourseRequest,
): Promise<DuplicateAuthoringCourseResponse> {
  const url = toAuthoringApiUrl(`/courses/${courseId}/duplicate`);
  const response = await http.post(
    url.toString(),
    JSON.stringify({
      course_name: request.courseName,
      org_id: request.orgId,
    }),
  );

  if (response.status !== 201) {
    throw new Error(
      await parseErrorMessage(response, `Failed to duplicate course (${response.status})`),
    );
  }

  const body = (await response.json()) as {
    id: string;
    course_name: string;
    org_id: string;
    status: string;
  };

  return {
    courseId: body.id,
    courseName: body.course_name,
    orgId: body.org_id,
    status: body.status,
  };
}
